/**
 * OrthogonalCprVisualization — renders a 2D cross-section of the axial DICOM
 * volume, resliced in real-time at the orientation defined by the centerline
 * frame at the current slice index.
 */

import 'vtk.js/Sources/Rendering/Profiles/Volume';

import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';
import vtkImageReslice from 'vtk.js/Sources/Imaging/Core/ImageReslice';
import vtkImageMapper from 'vtk.js/Sources/Rendering/Core/ImageMapper';
import vtkImageSlice from 'vtk.js/Sources/Rendering/Core/ImageSlice';
import vtkPiecewiseFunction from 'vtk.js/Sources/Common/DataModel/PiecewiseFunction';
import ImageConstants from 'vtk.js/Sources/Rendering/Core/ImageMapper/Constants';
import { InterpolationType } from 'vtk.js/Sources/Rendering/Core/ImageProperty/Constants';
import vtkWidgetManager from 'vtk.js/Sources/Widgets/Core/WidgetManager';
import { ViewTypes } from 'vtk.js/Sources/Widgets/Core/WidgetManager/Constants';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

import vtkPaintWidget from '../Paint/PaintWidget';
import vtkPaintFilter from './PaintFilter';

import { logDebug, isNullOrUndefined, getDUName, WidgetType } from './Common';
import {
    getTransferFunctionByColor,
    brushSizeToRadius,
    extractPaintedSlice,
} from './SegmentationShared';
import {
    createKonvaStage,
    attachCustomScreenshotListener,
    attachViewportScreenshotListener,
} from './DicomViewportShared';
import { buildCenterlinesPolyData, computeFrameAtIndex, buildResliceAxesFromFrame } from './CenterlineUtils';
import customImageInteractor from './CustomImageInteractor';
import {
    runReslice as runMaskReslice,
    newReslice as newMaskReslice,
    normalizeReslicedMaskToBinary,
    applyStoredSliceBuffer,
    DIAMETER_RESLICE_HALF_EXTENT_MM,
    ResliceInterpolationMode,
} from './MaskReslice';

import { useScreenshot } from '../Screenshot/Screenshot';
import { useInteraction } from './Interaction';
import { useDistanceWidget } from '../Distance/Distance';
import { useAngleWidget } from '../Angle/Angle';
import useDiameterLines from './DiameterLines';

const { SlicingMode } = ImageConstants;

const isDebugging = false;

function log(msg, ...params) {
    logDebug('OrthogonalCprVisualization', isDebugging, msg, ...params);
}

const CONTROL_POINT_RADIUS_CSS_PX = 4;

/**
 * Draw a control point circle at the center of the orthogonal view on the
 * overlay canvas to indicate the centerline point location.
 * @param {HTMLCanvasElement} canvas
 * @param {number} cx - Center X in CSS pixels.
 * @param {number} cy - Center Y in CSS pixels.
 * @param {boolean} isDragging - Whether the control point is being dragged.
 */
function drawControlPoint(canvas, cx, cy, isDragging) {
    const ctx2d = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx2d.clearRect(0, 0, w, h);

    const dpr = window.devicePixelRatio || 1;
    const cxPx = cx * dpr;
    const cyPx = cy * dpr;
    const radius = CONTROL_POINT_RADIUS_CSS_PX * dpr;

    ctx2d.beginPath();
    ctx2d.arc(cxPx, cyPx, radius, 0, 2 * Math.PI);
    ctx2d.fillStyle = isDragging ? '#ffff00' : '#00ff00';
    ctx2d.fill();
    ctx2d.strokeStyle = '#ffffff';
    ctx2d.lineWidth = 1.2 * dpr;
    ctx2d.stroke();
}

/**
 * @param {Object} props
 * @param {string} props.viewportId
 * @param {File[]} props.dicomFiles - Axial DICOM files.
 * @param {File[]} props.dicomMaskFiles - Axial segmentation mask files.
 * @param {boolean} props.showMasks
 * @param {vtkPolyData|null} props.wallCenterline - Wall centerline VTP polydata.
 * @param {vtkPolyData|null} [props.liveCenterlinePolyData] - Optional edited centerline polydata for the current branch.
 * @param {string} props.selectedBranch - 'Left', 'Right', or 'Cusp'.
 * @param {number|null} props.sliceIndex - Unified branch-local index.
 * @param {Object} props.segmentationColor - Blue or Red F# DU.
 * @param {(delta: number) => void} [props.onCenterlineSliceScroll]
 * @param {boolean} props.isSliceScroll
 * @param {Object} props.dicomLoader - Shared DicomLoader instance.
 * @param {boolean} props.isVisible
 * @param {Object} props.segmentationProps
 * @param {Object} [props.screenshotProps]
 * @param {Object} [props.interactionModeProps]
 * @param {(e: TouchEvent) => void} [props.onTouchStart]
 * @param {(e: TouchEvent) => void} [props.onTouchEnd]
 * @param {function} [props.onVisualizationReady]
 * @param {Object} props.eventNames - CPR event names (live centerline events).
 * @param {Object} [props.dicomEventNames] - DICOM event names (screenshot events).
 * @param {Object} [props.activeWidget] - F# WidgetType DU.
 * @param {Object} [props.activeMeasurement]
 * @param {Object} [props.lengthProps]
 * @param {Object} [props.angleProps]
 * @param {function} [props.centerlineSegmentationChangedCallback]
 * @param {Array.<[number, Object]>} [props.modifiedSegmentations]
 */
const OrthogonalCprVisualization = ({
    viewportId,
    dicomFiles,
    dicomMaskFiles,
    showMasks,
    wallCenterline,
    selectedBranch,
    sliceIndex,
    segmentationColor,
    onCenterlineSliceScroll,
    isSliceScroll,
    dicomLoader,
    isVisible,
    segmentationProps,
    screenshotProps,
    interactionModeProps,
    onTouchStart,
    onTouchEnd,
    onVisualizationReady,
    isEditable,
    isCenterlineEditable = true,
    eventNames,
    dicomEventNames,
    activeWidget = null,
    activeMeasurement = null,
    lengthProps,
    angleProps,
    centerlineSegmentationChangedCallback,
    modifiedSegmentations,
}) => {
    const containerRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const context = useRef(null);

    // Bumped after every stroke so `useDiameterLines` re-runs its effect and
    // recomputes the diameter overlay against the latest painted mask.
    const [diameterLinesTrigger, setDiameterLinesTrigger] = useState(false);

    // Cached live centerline from CprVisualization, tagged with branch name so
    // we can validate it against the currently-selected branch.
    const liveCenterlineRef = useRef({ branchName: null, polyData: null });

    // Keep refs for values that event handlers need at call-time without
    // re-registering the listener (avoids React effect ordering race conditions
    // where CprVisualization dispatches before our listener is re-attached).
    const sliceIndexRef = useRef(sliceIndex);
    sliceIndexRef.current = sliceIndex;

    // Whether centerline editing is the active mode.
    const isCenterlineEditableRef = useRef(isCenterlineEditable);
    isCenterlineEditableRef.current = isCenterlineEditable;

    const selectedBranchRef = useRef(selectedBranch);
    selectedBranchRef.current = selectedBranch;

    // Cache the last computed orientation frame so drag interaction can map
    // 2D pixel displacements to 3D world offsets.
    const lastFrameRef = useRef(null);

    // Drag interaction state.
    const dragRef = useRef({ isDragging: false, startX: 0, startY: 0 });

    // Store the callback in a ref so the paint widget's onEndInteraction
    // closure always calls the latest version without needing re-registration.
    const centerlineSegmentationChangedCallbackRef = useRef(null);
    centerlineSegmentationChangedCallbackRef.current = centerlineSegmentationChangedCallback;

    const modifiedSegmentationsRef = useRef(modifiedSegmentations);
    modifiedSegmentationsRef.current = modifiedSegmentations;

    const dicomMaskFilesRef = useRef(dicomMaskFiles);
    dicomMaskFilesRef.current = dicomMaskFiles;

    // Build per-branch centerline polydata from the wallCenterline VTP.
    const centerlinesPolyData = useMemo(() => {
        if (!wallCenterline) {
            return null;
        }
        log('Building centerline polydata from wallCenterline');
        return buildCenterlinesPolyData(wallCenterline).centerlinesPolyData;
    }, [wallCenterline]);

    // Select the active branch polydata (original, unless overridden by live edits).
    const originalBranchPolyData = useMemo(() => {
        if (!centerlinesPolyData || !selectedBranch) {
            return null;
        }
        const branchName = getDUName(selectedBranch);
        return centerlinesPolyData[branchName] || null;
    }, [centerlinesPolyData, selectedBranch]);

    // Stable event listener — registered once per eventNames, uses refs for
    // runtime checks.  This avoids the React effect-ordering race condition
    // where CprVisualization's branch-switch effect dispatches
    // LiveCenterlineDataChanged before a per-branch listener is re-attached.
    useEffect(() => {
        const eventName = eventNames?.LiveCenterlineDataChanged;
        if (!eventName) {
            return;
        }

        function onLiveCenterlineDataChanged(ev) {
            const { branchName, liveCenterline } = ev.detail;
            if (branchName === getDUName(selectedBranchRef.current) && liveCenterline) {
                liveCenterlineRef.current = { branchName, polyData: liveCenterline };
                if (context.current?.imageData) {
                    performReslice(liveCenterline);
                }
            }
        }
        window.addEventListener(eventName, onLiveCenterlineDataChanged);
        return () => window.removeEventListener(eventName, onLiveCenterlineDataChanged);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedBranchRef / context refs are stable by design; auto-adding them would re-register the listener and lose the race-condition guard described above.
    }, [eventNames]);

    // Reset live centerline cache when wallCenterline changes (new revision).
    useEffect(() => {
        liveCenterlineRef.current = { branchName: null, polyData: null };
    }, [wallCenterline]);

    /**
     * Apply any F#-side modifications for the current slice index onto the
     * freshly resliced mask output, producing the effective label map that
     * should be displayed and used as the paint target.
     */
    function applyModifiedSegmentationsToReslice(reslicedMaskImage) {
        if (!reslicedMaskImage) {
            return reslicedMaskImage;
        }
        const modMap = modifiedSegmentationsRef.current;
        const idx = sliceIndexRef.current;
        if (!modMap || !modMap.length || idx == null) {
            return reslicedMaskImage;
        }

        const roundedIdx = Math.round(idx);
        const match = modMap.find(([k]) => k === roundedIdx);
        if (!match) {
            return reslicedMaskImage;
        }

        const [, data] = match;
        applyStoredSliceBuffer(reslicedMaskImage, data.buffer);
        return reslicedMaskImage;
    }

    /**
     * Core reslice logic, extracts a 2D cross-section from the axial volume
     * at the orientation defined by the given centerline polydata at the
     * current slice index.
     */
    function performReslice(polyData) {
        const ctx = context.current;
        const currentSliceIndex = sliceIndexRef.current;
        if (!ctx || !ctx.imageData || !polyData || currentSliceIndex == null) {
            return;
        }

        const numPoints = polyData.getPoints().getNumberOfPoints();
        if (numPoints === 0) {
            return;
        }

        const idx = Math.max(0, Math.min(Math.round(currentSliceIndex), numPoints - 1));

        // Compute the orientation frame from the actual point positions.
        // During live editing the positions are updated but tensors are not,
        // so the tangent, bitangent and normal must be derived from the
        // current (possibly edited) geometry.
        const frame = computeFrameAtIndex(polyData, idx);
        lastFrameRef.current = frame;
        const resliceAxes = buildResliceAxesFromFrame(frame);

        const spacing = ctx.imageData.getSpacing();
        const minSpacing = Math.min(spacing[0], spacing[1], spacing[2]);
        const physicalHalfExtent = DIAMETER_RESLICE_HALF_EXTENT_MM;
        const halfPixels = Math.ceil(physicalHalfExtent / minSpacing);

        ctx.reslice.setResliceAxes(resliceAxes);
        ctx.reslice.setOutputSpacing([minSpacing, minSpacing, minSpacing]);
        ctx.reslice.setOutputOrigin([-physicalHalfExtent, -physicalHalfExtent, 0]);
        ctx.reslice.setOutputExtent([0, 2 * halfPixels, 0, 2 * halfPixels, 0, 0]);
        ctx.reslice.setOutputDimensionality(2);
        ctx.reslice.update();

        const reslicedImage = ctx.reslice.getOutputData();
        if (reslicedImage) {
            ctx.mapper.setInputData(reslicedImage);
            if (ctx.painter) {
                ctx.painter.setBackgroundImage(reslicedImage);
            }
        }

        // Update mask reslice with identical axes.
        if (ctx.maskImageData) {
            const reslicedMask = runMaskReslice(ctx.maskReslice, ctx.maskImageData, frame);
            if (reslicedMask) {
                // Binarize first so downstream consumers (paint filter,
                // display opacity function, stored modifications) all see
                // the same label convention.
                normalizeReslicedMaskToBinary(reslicedMask);
                applyModifiedSegmentationsToReslice(reslicedMask);
                if (ctx.painter) {
                    ctx.painter.setLabelMap(reslicedMask);
                    ctx.maskMapper.setInputConnection(ctx.painter.getOutputPort());
                } else {
                    ctx.maskMapper.setInputData(reslicedMask);
                }
            }
        }

        // Pin the paint widget manipulator to the reslice plane (local Z=0).
        if (ctx.paintWidget) {
            ctx.paintWidget.getManipulator().setUserOrigin([0, 0, 0]);
            ctx.paintWidget.getManipulator().setUserNormal([0, 0, 1]);
            ctx.paintHandle?.updateRepresentationForRender();
        }

        fitCameraToImage(ctx);
        ctx.renderWindow.render();
        updateControlPointMarker();
    }

    /**
     * Compute the display (CSS pixel) coordinates of the centerline position
     * in the reslice output.  The centerline point is always at the reslice
     * origin (0, 0) in physical coordinates.
     * @returns {{ x: number, y: number } | null}
     */
    function getCenterDisplayCoords() {
        const container = containerRef.current;
        if (!container) return null;

        // The reslice output is always symmetric around the centerline point
        // (origin 0,0) and fitCameraToImage centres the camera on the image,
        // so the control point is always at the container centre.
        return { x: container.clientWidth / 2, y: container.clientHeight / 2 };
    }

    /**
     * Redraw the control point overlay.
     * @param {{ x: number, y: number }} [pos] - Override position (CSS pixels).
     */
    function updateControlPointMarker(pos) {
        const canvas = overlayCanvasRef.current;
        if (!canvas || !isEditable) return;

        // The marker is only shown while centerline editing is the active mode; clear it otherwise.
        if (!isCenterlineEditableRef.current) {
            const ctx2d = canvas.getContext('2d');
            ctx2d.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        const p = pos || getCenterDisplayCoords();
        if (!p) return;

        drawControlPoint(canvas, p.x, p.y, dragRef.current.isDragging);
    }

    /**
     * Convert a mouse pixel displacement to a 3D world offset using the
     * current reslice frame's B (horizontal) and N (vertical) axes.
     *
     * The reslice output has uniform spacing and the camera is set up with
     * parallel projection and viewUp = (0, -1, 0).  The physical size
     * covered per CSS pixel is (parallelScale * 2) / containerHeight.
     *
     * @param {number} dxPx - Horizontal pixel displacement (right = positive).
     * @param {number} dyPx - Vertical pixel displacement (down = positive).
     * @returns {number[]|null} World offset [dx, dy, dz] or null.
     */
    function pixelDisplacementToWorldOffset(dxPx, dyPx) {
        const frame = lastFrameRef.current;
        const ctx = context.current;
        if (!frame || !ctx?.renderer) return null;

        const camera = ctx.renderer.getActiveCamera();
        const pscale = camera.getParallelScale();
        const container = containerRef.current;
        if (!container) return null;

        const containerH = container.clientHeight || 1;
        const mmPerPx = (pscale * 2) / containerH;

        // Camera viewUp is (0,-1,0) and looks along -Z.
        // Camera right = cross(viewDir, viewUp) = cross((0,0,-1),(0,-1,0)) = (-1,0,0),
        // so CSS-right = VTK-X-negative = -B.  Negate dxPx accordingly.
        // CSS-down = VTK-Y-positive = N-positive (unchanged).
        const dB = -dxPx * mmPerPx;
        const dN = dyPx * mmPerPx;

        return [
            frame.B[0] * dB + frame.N[0] * dN,
            frame.B[1] * dB + frame.N[1] * dN,
            frame.B[2] * dB + frame.N[2] * dN,
        ];
    }

    /**
     * Dispatch an OrthogonalCenterlineEdited event to CprVisualization.
     * @param {number[]} worldOffset - [dx, dy, dz] offset in world coordinates.
     */
    function dispatchOrthogonalEdit(worldOffset) {
        const eventName = eventNames?.OrthogonalCenterlineEdited;
        if (!eventName) return;

        const currentSliceIndex = sliceIndexRef.current;
        if (currentSliceIndex == null) return;

        const branchName = getDUName(selectedBranchRef.current);
        window.dispatchEvent(new CustomEvent(eventName, {
            detail: {
                branchName,
                pointIndex: Math.round(currentSliceIndex),
                worldOffset,
                viewportId,
                segmentationColor: getDUName(segmentationColor),
            }
        }));
    }

    /**
     * Dispatch an OrthogonalSliceChanged event so the stretched CPR view can
     * highlight the control point corresponding to the current orthogonal slice.
     * Passing `null` clears this view's highlight on the consumer side.
     * @param {number|null} pointIndex - Centerline point index, or null to clear.
     */
    function dispatchSliceChanged(pointIndex) {
        const eventName = eventNames?.OrthogonalSliceChanged;
        if (!eventName) return;

        const branchName = getDUName(selectedBranchRef.current);
        window.dispatchEvent(new CustomEvent(eventName, {
            detail: {
                branchName,
                pointIndex,
                viewportId,
                segmentationColor: getDUName(segmentationColor),
            },
        }));
    }

    /**
     * Perform the reslice and render using the current effective polydata.
     */
    const updateReslice = useCallback(() => {
        const branchName = getDUName(selectedBranch);
        const live = liveCenterlineRef.current;
        const effectivePolyData =
            (live.branchName === branchName ? live.polyData : null) || originalBranchPolyData;
        performReslice(effectivePolyData);
    }, [originalBranchPolyData, sliceIndex, selectedBranch]);

    /**
     * Reset the camera to fit the resliced 2D image.
     */
    function fitCameraToImage(ctx) {
        if (!ctx?.renderer || !ctx?.mapper) {
            return;
        }

        const imageData = ctx.mapper.getInputData();
        if (!imageData) {
            return;
        }

        const bounds = imageData.getBounds();
        const imageWidth = bounds[1] - bounds[0];
        const imageHeight = bounds[3] - bounds[2];

        if (imageWidth <= 0 || imageHeight <= 0) {
            return;
        }

        const containerWidth = containerRef.current?.clientWidth || 1;
        const containerHeight = containerRef.current?.clientHeight || 1;
        const containerAspect = containerWidth / containerHeight;
        const imageAspect = imageWidth / imageHeight;

        const camera = ctx.renderer.getActiveCamera();
        camera.setParallelProjection(true);

        const cx = (bounds[0] + bounds[1]) / 2;
        const cy = (bounds[2] + bounds[3]) / 2;
        camera.setFocalPoint(cx, cy, 0);
        camera.setPosition(cx, cy, 1);
        camera.setViewUp(0, -1, 0);

        if (containerAspect > imageAspect) {
            camera.setParallelScale(imageHeight / 2);
        } else {
            camera.setParallelScale(imageWidth / (2 * containerAspect));
        }
        camera.setClippingRange(0.01, 10);
    }

    // --- Mount / unmount ---
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;

        // Konva overlay + resize-callback set, shared with DicomVisualization.
        // Diameter lines, screenshot composition, and widget overlays all
        // depend on the konvaStage / resizeCallbacks members of the context.
        const resizeCallbacks = new Set();
        const { stage: konvaStage } = createKonvaStage(container, viewportId);

        const genericRenderWindow = vtkGenericRenderWindow.newInstance();
        genericRenderWindow.setContainer(container);
        genericRenderWindow.resize();

        const renderer = genericRenderWindow.getRenderer();
        const renderWindow = genericRenderWindow.getRenderWindow();

        renderer.setBackground(0, 0, 0);

        const camera = renderer.getActiveCamera();
        camera.setParallelProjection(true);
        camera.setClippingRange(0.01, 10);

        // --- Image reslice filter ---
        const reslice = vtkImageReslice.newInstance({
            outputDimensionality: 2,
            interpolationMode: ResliceInterpolationMode.LINEAR,
            autoCropOutput: false,
            transformInputSampling: true,
            backgroundColor: [0, 0, 0, 0],
        });

        const mapper = vtkImageMapper.newInstance();
        mapper.setSlicingMode(SlicingMode.Z);
        mapper.setSliceAtFocalPoint(false);
        mapper.setSlice(0);

        const actor = vtkImageSlice.newInstance();
        actor.setMapper(mapper);
        actor.getProperty().setColorWindow(1200);
        actor.getProperty().setColorLevel(80);
        actor.getProperty().setInterpolationType(InterpolationType.NEAREST);
        
        // Exclude image and mask actors from hardware picking so the hardware
        // selector can reach widget handle glyphs.
        actor.setPickable(false);

        renderer.addActor(actor);

        // --- Mask reslice filter ---

        const maskReslice = newMaskReslice();

        const maskMapper = vtkImageMapper.newInstance();
        maskMapper.setSlicingMode(SlicingMode.Z);
        maskMapper.setSliceAtFocalPoint(false);
        maskMapper.setSlice(0);

        const maskActor = vtkImageSlice.newInstance();
        maskActor.setMapper(maskMapper);
        maskActor.setPickable(false);

        const opFun = vtkPiecewiseFunction.newInstance();
        opFun.addPoint(0, 0);
        opFun.addPoint(1, 1);

        const cFun = getTransferFunctionByColor(segmentationColor);

        const maskProp = maskActor.getProperty();
        maskProp.setRGBTransferFunction(0, cFun);
        maskProp.setScalarOpacity(0, opFun);
        maskProp.setInterpolationTypeToLinear();
        maskProp.setUseLookupTableScalarRange(true);
        maskProp.setUseLabelOutline(true);
        if (segmentationProps) {
            maskProp.setLabelOutlineThickness(segmentationProps.segmentationBorderThickness);
            maskProp.setLabelOutlineOpacity(segmentationProps.segmentationBorderOpacity);
            maskProp.setOpacity(segmentationProps.segmentationOpacity);
        }

        renderer.addActor(maskActor);

        // --- Paint widget and filter ---
        const widgetManager = vtkWidgetManager.newInstance();
        widgetManager.setRenderer(renderer);

        const paintWidget = vtkPaintWidget.newInstance();
        const paintHandle = widgetManager.addWidget(paintWidget, ViewTypes.SLICE);
        const painter = vtkPaintFilter.newInstance();
        painter.setSlicingMode(SlicingMode.Z);

        // The reslice plane sits at local Z=0 — fix the manipulator there so
        // the paint widget paints on the displayed 2D surface.
        paintWidget.getManipulator().setUserOrigin([0, 0, 0]);
        paintWidget.getManipulator().setUserNormal([0, 0, 1]);

        paintHandle.onStartInteractionEvent(() => {
            const point = paintWidget.getWidgetState().getTrueOrigin();
            const controlKey = paintWidget.getWidgetState().getControlKey();
            // Painting always happens on the single reslice plane (Z=0).
            const paintPoint = [point[0], point[1], 0];

            // Resolve the paint label ourselves, as I think we can't rely on
            // vtkPaintFilter.setLabelForClickedPoint here: its BUMP branch
            // checks for a slice that contains BOTH 0 and 1 values, and if
            // that check doesn't pass (like with a freshly-resliced
            // 2D label map) it falls through to `label = 1 - pickedLabel`.
            // Compute that explicitly so the result is identical whether the
            // slice is mixed, fully empty, or fully filled.
            const modeName = getDUName(segmentationProps?.paintingMode);
            let label;
            if (modeName === 'Paint') {
                label = controlKey ? 0 : 1;
            } else if (modeName === 'Erase') {
                label = 0;
            } else {
                const labelMap = painter.getLabelMap();
                label = 1;
                if (labelMap) {
                    const scalars = labelMap.getPointData().getScalars().getData();
                    const origin = labelMap.getOrigin();
                    const spacing = labelMap.getSpacing();
                    const [nx, ny] = labelMap.getDimensions();
                    const ix = Math.max(
                        0,
                        Math.min(Math.round((paintPoint[0] - origin[0]) / spacing[0]), nx - 1),
                    );
                    const iy = Math.max(
                        0,
                        Math.min(Math.round((paintPoint[1] - origin[1]) / spacing[1]), ny - 1),
                    );
                    label = scalars[ix + iy * nx] !== 0 ? 1 : 0;
                }
            }
            painter.setLabel(label);
            painter.startStroke();
            painter.addPoint(paintPoint);
        });

        paintHandle.onInteractionEvent(() => {
            const point = paintWidget.getWidgetState().getTrueOrigin();
            painter.addPoint([point[0], point[1], 0]);
        });

        paintHandle.onEndInteractionEvent(() => {
            painter.endStroke()?.then(() => {
                const callback = centerlineSegmentationChangedCallbackRef.current;
                const centerlineIdx = sliceIndexRef.current;
                if (!callback || centerlineIdx == null) {
                    return;
                }

                const paintedOutput = painter.getOutputData();
                if (!paintedOutput) {
                    return;
                }

                const roundedIdx = Math.round(centerlineIdx);
                const data = extractPaintedSlice(paintedOutput, 0);
                callback(roundedIdx, data);
                // Poke the diameter-line effect so it recomputes against
                // the new mask.
                setDiameterLinesTrigger((prev) => !prev);
            });
        });

        // --- Interactor ---
        const istyle = customImageInteractor.newInstance();
        istyle.setContainer(container);
        istyle.setRenderWindow(renderWindow);
        istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        if (onCenterlineSliceScroll) {
            istyle.setSliceScrollCallback(onCenterlineSliceScroll);
        }
        istyle.setInteractionMode('IMAGE_SLICING');
        renderWindow.getInteractor().setInteractorStyle(istyle);

        // Touch events.
        const combinedTouchStart = (e) => {
            onTouchStart?.(e);
            istyle.handleTouchStart(e);
        };
        const combinedTouchEnd = (e) => {
            onTouchEnd?.(e);
            istyle.handleTouchEnd(e);
        };
        container.addEventListener('touchstart', combinedTouchStart);
        container.addEventListener('touchend', combinedTouchEnd);
        container.addEventListener('touchmove', istyle.handleTouchMove);

        // Resize observer: keep the VTK render window, the Konva stage, and
        // any registered resize callbacks (e.g. diameter lines) in sync.
        const resizeObserver = new ResizeObserver(() => {
            const ctx = context.current;
            if (!containerRef.current || !ctx?.genericRenderWindow || !ctx.mapper.getInputData()) {
                return;
            }
            ctx.genericRenderWindow.resize();
            const { width, height } = containerRef.current.getBoundingClientRect();
            for (const callback of ctx.resizeCallbacks) {
                callback(width, height);
            }
            if (ctx.konvaStage) {
                ctx.konvaStage.size({ width, height });
                ctx.konvaStage.batchDraw();
            }
            fitCameraToImage(ctx);
            ctx.renderWindow.render();
            updateControlPointMarker();
        });
        resizeObserver.observe(container);

        context.current = {
            viewportId,
            genericRenderWindow,
            renderer,
            renderWindow,
            reslice,
            mapper,
            actor,
            maskReslice,
            maskMapper,
            maskActor,
            istyle,
            resizeObserver,
            resizeCallbacks,
            konvaStage,
            imageData: null,
            maskImageData: null,
            combinedTouchStart,
            combinedTouchEnd,
            widgetManager,
            paintWidget,
            paintHandle,
            painter,
        };

        context.current.detachCustomScreenshotListener = attachCustomScreenshotListener(
            context,
            viewportId,
            dicomEventNames?.CaptureCustomScreenshot,
        );

        return () => {
            const ctx = context.current;
            resizeObserver.disconnect();
            if (container) {
                container.removeEventListener('touchstart', combinedTouchStart);
                container.removeEventListener('touchend', combinedTouchEnd);
                container.removeEventListener('touchmove', istyle.handleTouchMove);
            }
            ctx?.detachCustomScreenshotListener?.();
            // Stop the interactor first so any animation frames already in
            // flight don't fire render() against a half-deleted scene graph.
            renderWindow.getInteractor()?.disable();
            widgetManager.removeWidgets();
            widgetManager.releaseFocus();
            widgetManager.delete();
            genericRenderWindow.delete();
            context.current = null;
        };
    }, [viewportId]);

    // --- Overlay canvas for control point and drag interaction ---
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !isEditable) {
            // Remove existing overlay when this is not a centerline cross-section.
            if (overlayCanvasRef.current?.parentNode) {
                overlayCanvasRef.current.parentNode.removeChild(overlayCanvasRef.current);
                overlayCanvasRef.current = null;
            }
            return;
        }

        // Create an overlay canvas that sits on top of the VTK canvas.
        const overlay = document.createElement('canvas');
        overlay.classList.add('orthogonal-edit-overlay');
        container.appendChild(overlay);
        overlayCanvasRef.current = overlay;

        function syncOverlaySize() {
            const dpr = window.devicePixelRatio || 1;
            overlay.width = container.clientWidth * dpr;
            overlay.height = container.clientHeight * dpr;
            updateControlPointMarker();
        }
        syncOverlaySize();

        const overlayResizeObs = new ResizeObserver(syncOverlaySize);
        overlayResizeObs.observe(container);

        // --- Distance-based picking: only start a drag if mousedown is near the control point ---
        const PICK_RADIUS_PX = CONTROL_POINT_RADIUS_CSS_PX + 1; // fill radius + half stroke-width

        function isNearControlPoint(mouseX, mouseY) {
            const center = getCenterDisplayCoords();
            if (!center) return false;
            const dx = mouseX - center.x;
            const dy = mouseY - center.y;
            return Math.sqrt(dx * dx + dy * dy) <= PICK_RADIUS_PX;
        }

        function onMouseDown(ev) {
            if (ev.button !== 0) return;

            // Dragging is only allowed while centerline editing is the active mode.
            if (!isCenterlineEditableRef.current) return;

            const rect = container.getBoundingClientRect();
            const mx = ev.clientX - rect.left;
            const my = ev.clientY - rect.top;

            if (!isNearControlPoint(mx, my)) return;

            ev.stopPropagation();
            ev.preventDefault();

            dragRef.current = { isDragging: true, startX: ev.clientX, startY: ev.clientY };
            updateControlPointMarker();
        }

        function onMouseMove(ev) {
            if (!dragRef.current.isDragging) {
                const rect = container.getBoundingClientRect();
                const mx = ev.clientX - rect.left;
                const my = ev.clientY - rect.top;
                dragTarget.style.cursor =
                    isCenterlineEditableRef.current && isNearControlPoint(mx, my) ? 'pointer' : '';
                return;
            }

            ev.stopPropagation();
            ev.preventDefault();

            // Visual feedback only by moving the marker, without recomputing dependent views.
            const rect = container.getBoundingClientRect();
            const mx = ev.clientX - rect.left;
            const my = ev.clientY - rect.top;
            updateControlPointMarker({ x: mx, y: my });
        }

        function onMouseUp(ev) {
            if (!dragRef.current.isDragging) return;

            ev.stopPropagation();
            ev.preventDefault();

            const dxPx = ev.clientX - dragRef.current.startX;
            const dyPx = ev.clientY - dragRef.current.startY;

            dragRef.current = { isDragging: false, startX: 0, startY: 0 };

            // Skip dispatch when there is no meaningful movement.
            if (Math.abs(dxPx) < 1 && Math.abs(dyPx) < 1) {
                updateControlPointMarker();
                return;
            }

            const worldOffset = pixelDisplacementToWorldOffset(dxPx, dyPx);
            if (worldOffset) {
                dispatchOrthogonalEdit(worldOffset);
            }
            updateControlPointMarker();
        }

        // Attach drag handlers to the VTK canvas (the first canvas child of the container).
        // We use the actual rendering canvas so we intercept events before the VTK interactor.
        const vtkCanvas = container.querySelector('canvas:not([style*="pointer-events"])');
        const dragTarget = vtkCanvas || container;

        dragTarget.addEventListener('mousedown', onMouseDown, true);
        window.addEventListener('mousemove', onMouseMove, true);
        window.addEventListener('mouseup', onMouseUp, true);

        return () => {
            overlayResizeObs.disconnect();
            dragTarget.removeEventListener('mousedown', onMouseDown, true);
            window.removeEventListener('mousemove', onMouseMove, true);
            window.removeEventListener('mouseup', onMouseUp, true);
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            overlayCanvasRef.current = null;
        };
    }, [viewportId, isEditable]);

    // Show/hide the marker when the editing mode changes.
    useEffect(() => {
        updateControlPointMarker();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- updateControlPointMarker reads refs.
    }, [isCenterlineEditable]);

    // --- Load axial DICOM volume ---
    useEffect(() => {
        if (!dicomFiles?.length || !dicomLoader || !context.current) {
            return;
        }

        log('Loading axial DICOM volume');
        dicomLoader.tryGetImage(dicomFiles).then((imageData) => {
            if (isNullOrUndefined(imageData) || !context.current) {
                return;
            }

            context.current.imageData = imageData;
            context.current.reslice.setInputData(imageData);
            updateReslice();
            onVisualizationReady?.([viewportId, true]);
        });
    }, [dicomFiles, viewportId]);

    // --- Load axial mask volume ---
    useEffect(() => {
        if (!dicomMaskFiles?.length || !dicomLoader || !context.current) {
            return;
        }

        log('Loading axial mask volume');
        dicomLoader.tryGetMask(dicomMaskFiles).then((imageData) => {
            if (isNullOrUndefined(imageData) || !context.current) {
                return;
            }

            context.current.maskImageData = imageData;
            context.current.maskReslice.setInputData(imageData);
            updateReslice();
        });
    }, [dicomMaskFiles]);

    // --- Update reslice when slice index, branch, or stored modifications change ---
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateReslice is recreated whenever its own deps change; listing it here is sufficient even though some reads inside it use refs.
    useEffect(() => {
        updateReslice();
    }, [updateReslice, modifiedSegmentations]);

    const lastDispatchedSliceRef = useRef(null);
    useEffect(() => {
        const next = (isEditable && sliceIndex != null) ? Math.round(sliceIndex) : null;
        if (next === lastDispatchedSliceRef.current) return;
        lastDispatchedSliceRef.current = next;
        dispatchSliceChanged(next);
    }, [sliceIndex, selectedBranch, isEditable]);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional unmount-only cleanup; closure captures of eventNames/selectedBranchRef/segmentationColor/viewportId at mount time are correct here.
    useEffect(() => () => {
        if (lastDispatchedSliceRef.current != null) {
            dispatchSliceChanged(null);
        }
    }, []);

    useEffect(() => {
        setDiameterLinesTrigger((prev) => !prev);
    }, [sliceIndex, selectedBranch, modifiedSegmentations, wallCenterline]);

    // --- Show/hide mask ---
    useEffect(() => {
        const ctx = context.current;
        if (!ctx || !ctx.maskMapper.getInputData()) {
            return;
        }
        ctx.maskActor.setVisibility(!!showMasks);
        ctx.renderWindow.render();
    }, [showMasks]);

    // --- Visibility ---
    useEffect(() => {
        if (!context.current || !isVisible) {
            return;
        }
        fitCameraToImage(context.current);
        context.current.renderWindow.render();
    }, [isVisible]);

    // --- Scroll callback update ---
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setSliceScrollCallback(onCenterlineSliceScroll || null);
        }
    }, [onCenterlineSliceScroll]);

    // --- Slice scroll mode ---
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
    }, [isSliceScroll]);

    // --- Segmentation color update ---
    useEffect(() => {
        const ctx = context.current;
        if (!ctx?.maskActor || !ctx.maskMapper.getInputData()) {
            return;
        }
        const cFun = getTransferFunctionByColor(segmentationColor);
        ctx.maskActor.getProperty().setRGBTransferFunction(0, cFun);
        ctx.renderWindow.render();
    }, [segmentationColor]);

    // --- Segmentation props / active-widget update ---
    useEffect(() => {
        const ctx = context.current;
        if (!ctx?.maskActor || !segmentationProps || !ctx.maskMapper.getInputData()) {
            return;
        }

        const prop = ctx.maskActor.getProperty();
        prop.setLabelOutlineThickness(
            segmentationProps.segmentationBorderOpacity === 0
                ? 0
                : segmentationProps.segmentationBorderThickness
        );
        prop.setLabelOutlineOpacity(segmentationProps.segmentationBorderOpacity);
        prop.setOpacity(segmentationProps.segmentationOpacity);

        if (ctx.painter) {
            ctx.painter.setPaintingMode(getDUName(segmentationProps.paintingMode));
            const brushRadius = brushSizeToRadius(segmentationProps.paintingBrushSize);
            ctx.paintWidget.setRadius(brushRadius);
            ctx.painter.setRadius(brushRadius);
        }

        if (ctx.widgetManager && ctx.paintHandle) {
            const paintingActive =
                getDUName(activeWidget) === WidgetType.SEGMENTATION_WIDGET &&
                segmentationProps.paintingEnabled;

            if (paintingActive) {
                ctx.widgetManager.grabFocus(ctx.paintWidget);
            } else {
                ctx.widgetManager.releaseFocus();
            }
            ctx.paintHandle.setVisibility(segmentationProps.paintingEnabled);
            ctx.paintHandle.updateRepresentationForRender();
        }

        ctx.renderWindow.render();
    }, [segmentationProps, activeWidget]);

    // Screenshot capture hook, same as DicomVisualization uses for the
    // viewport's screenshot button integration.
    const [captureScreenshot, ...screenshotInteraction] = useScreenshot(
        containerRef.current,
        context.current,
        null,
        screenshotProps,
    );

    // Interaction mode hook.
    useInteraction(
        null,
        interactionModeProps,
        null,
        null,
        screenshotInteraction,
        captureScreenshot,
        null,
        null,
        null,
        null,
    );

    // Length measurement widget, shares the same hook used by DicomVisualization.
    // useDistanceWidget tolerates null/undefined props.
    useDistanceWidget(
        context.current,
        lengthProps,
        sliceIndex,
        activeWidget,
        { name: 'Axial' },
    );

    // Angle measurement widget, shares the same hook used by DicomVisualization.
    // useAngleWidget tolerates null/undefined props.
    useAngleWidget(
        context.current,
        angleProps,
        sliceIndex,
        activeWidget,
        { name: 'Axial' },
    );

    // Diameter-line overlay
    useDiameterLines(
        context.current,
        0,
        centerlineSegmentationChangedCallback,
        dicomMaskFilesRef.current,
        !!activeMeasurement,
        diameterLinesTrigger,
    );

    return <div className="layout-view-view-container" ref={containerRef} />;
};

export default OrthogonalCprVisualization;
